import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui';
import { Modal } from '@/components/ui/Modal';
import { leadImportService } from '@/services/leadImportService';
import type { LeadProvider, LeadProviderType } from '@/types/leadImport';
import styles from './LeadProvidersPage.module.css';

const PROVIDER_TYPE_OPTIONS: { value: LeadProviderType; label: string }[] = [
  { value: 'CNES', label: 'CNES' },
  { value: 'GOOGLE_PLACES', label: 'Google Places' },
  { value: 'CNPJA', label: 'CNPJA' },
  { value: 'SERPER', label: 'Serper' },
  { value: 'APOLLO', label: 'Apollo' },
  { value: 'CFM', label: 'CFM' },
  { value: 'APIFY_IMPORT', label: 'Apify Import' },
  { value: 'META_ADS', label: 'Meta Ads' },
  { value: 'MANUAL', label: 'Manual' },
  { value: 'WEBSITE', label: 'Website' },
];

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

interface ProviderFormState {
  name: string;
  type: LeadProviderType;
  config: string;
  isActive: boolean;
}

const INITIAL_FORM: ProviderFormState = {
  name: '',
  type: 'MANUAL',
  config: '',
  isActive: true,
};

/**
 * Admin page for managing lead providers (CRUD).
 *
 * Features:
 * - Table listing all providers
 * - Create new provider via modal form
 * - Edit existing provider (name, config, active toggle)
 */
export function LeadProvidersPage() {
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<LeadProvider | null>(null);
  const [form, setForm] = useState<ProviderFormState>(INITIAL_FORM);

  const { data: providers, isLoading } = useQuery({
    queryKey: ['lead-providers'],
    queryFn: () => leadImportService.getProviders(),
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; type: LeadProviderType; config?: string }) =>
      leadImportService.createProvider(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-providers'] });
      handleCloseModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string; config?: string; isActive: boolean } }) =>
      leadImportService.updateProvider(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-providers'] });
      handleCloseModal();
    },
  });

  const handleOpenCreate = useCallback(() => {
    setEditingProvider(null);
    setForm(INITIAL_FORM);
    setIsModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback((provider: LeadProvider) => {
    setEditingProvider(provider);
    setForm({
      name: provider.name,
      type: provider.type,
      config: '',
      isActive: provider.isActive,
    });
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingProvider(null);
    setForm(INITIAL_FORM);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!form.name.trim()) return;

    if (editingProvider) {
      updateMutation.mutate({
        id: editingProvider.id,
        data: {
          name: form.name,
          config: form.config || undefined,
          isActive: form.isActive,
        },
      });
    } else {
      createMutation.mutate({
        name: form.name,
        type: form.type,
        config: form.config || undefined,
      });
    }
  }, [form, editingProvider, createMutation, updateMutation]);

  const handleToggleActive = useCallback(
    (provider: LeadProvider) => {
      updateMutation.mutate({
        id: provider.id,
        data: {
          name: provider.name,
          isActive: !provider.isActive,
        },
      });
    },
    [updateMutation],
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>Provedores de Leads</h1>
          <p className={styles.subtitle}>
            Gerencie as fontes de importação de leads.
          </p>
        </div>
        <Button variant="primary" onClick={handleOpenCreate}>
          Novo Provedor
        </Button>
      </header>

      {isLoading ? (
        <div className={styles.emptyState} role="status" aria-busy="true">
          <p>Carregando provedores...</p>
        </div>
      ) : !providers || providers.length === 0 ? (
        <div className={styles.emptyState} role="status">
          <p>Nenhum provedor cadastrado.</p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Criado em</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {providers.map((provider: LeadProvider) => (
                <tr key={provider.id}>
                  <td><strong>{provider.name}</strong></td>
                  <td>{provider.type}</td>
                  <td>
                    <span
                      className={styles.badge}
                      style={{
                        color: provider.isActive ? '#155724' : '#721C24',
                        backgroundColor: provider.isActive ? '#D4EDDA' : '#F8D7DA',
                      }}
                    >
                      {provider.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td>
                    <span className={styles.dateText}>
                      {formatDate(provider.createdAt)}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenEdit(provider)}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(provider)}
                        isLoading={updateMutation.isPending}
                      >
                        {provider.isActive ? 'Desativar' : 'Ativar'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingProvider ? 'Editar Provedor' : 'Novo Provedor'}
        size="sm"
      >
        <div>
          <div className={styles.formGroup}>
            <label htmlFor="provider-name" className={styles.formLabel}>
              Nome
            </label>
            <input
              id="provider-name"
              type="text"
              className={styles.formInput}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Nome do provedor"
            />
          </div>

          {!editingProvider && (
            <div className={styles.formGroup}>
              <label htmlFor="provider-type" className={styles.formLabel}>
                Tipo
              </label>
              <select
                id="provider-type"
                className={styles.formSelect}
                value={form.type}
                onChange={(e) =>
                  setForm((f) => ({ ...f, type: e.target.value as LeadProviderType }))
                }
              >
                {PROVIDER_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="provider-config" className={styles.formLabel}>
              Configuração (JSON)
            </label>
            <input
              id="provider-config"
              type="text"
              className={styles.formInput}
              value={form.config}
              onChange={(e) => setForm((f) => ({ ...f, config: e.target.value }))}
              placeholder='{"apiKey": "..."}'
            />
          </div>

          {editingProvider && (
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, isActive: e.target.checked }))
                  }
                />{' '}
                Ativo
              </label>
            </div>
          )}

          <div className={styles.formActions}>
            <Button variant="ghost" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              isLoading={createMutation.isPending || updateMutation.isPending}
              disabled={!form.name.trim()}
            >
              {editingProvider ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
